import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Upload, Button, List, Typography } from 'antd';
import { UploadOutlined, EyeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { uploadDocument } from '@app/api/nominalPayments.api';

const { Text } = Typography;

interface UploadedFile {
  id?: number;
  name: string;
  file: File | string;
  url?: string;
}

interface DocUploadInFRSProps {
  Id: number | null;
  initialFiles?: { id: number; name: string; image_url: string }[];
}

const DocUploadInFRS: React.FC<DocUploadInFRSProps> = ({ Id, initialFiles = [] }) => {
  const [form] = Form.useForm();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const reuploadIndexRef = useRef<number | null>(null);
  const hiddenUploadRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (initialFiles.length) {
      const mappedFiles: UploadedFile[] = initialFiles.map((item) => ({
        id: item.id,
        name: item.name,
        file: item.image_url,
        url: item.image_url,
      }));
      setUploadedFiles(mappedFiles);
    }
  }, [initialFiles]);

  const normFile = (e: any) => {
    return Array.isArray(e) ? e : e?.fileList;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const fileItem = values.file?.[0];

      if (fileItem && fileItem.originFileObj) {
        const payload: any = {
          name: values.fileName,
          file: fileItem.originFileObj,
        };

        const response = await uploadDocument(Number(Id), payload);

        const fileData = Array.isArray(response.data) ? response.data[0] : response.data;
        const newFile: UploadedFile = {
          id: fileData.id,
          name: fileData.name,
          file: fileData.image_url,
          url: fileData.image_url,
        };

        setUploadedFiles((prev) => [...prev, newFile]);
        form.resetFields();
      }
    } catch (err) {
      console.error('Failed to upload document:', err);
    }
  };

  const handleRemove = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleView = (file: File | string) => {
    const url = typeof file === 'string' ? file : URL.createObjectURL(file);
    window.open(url, '_blank');
  };

  const handleReuploadClick = (index: number) => {
    reuploadIndexRef.current = index;
    hiddenUploadRef.current?.click();
  };

  const handleHiddenFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || reuploadIndexRef.current === null) return;

    const index = reuploadIndexRef.current;
    const existing = uploadedFiles[index];

    try {
      const payload = {
        id: existing.id,
        name: existing.name,
        file,
      };

      const response = await uploadDocument(Number(Id), payload);
      const fileData = Array.isArray(response.data) ? response.data[0] : response.data;

      const updated = [...uploadedFiles];
      updated[index] = {
        id: fileData.id,
        name: fileData.name,
        file: fileData.image_url,
        url: fileData.image_url,
      };

      setUploadedFiles(updated);
    } catch (err) {
      console.error('Re-upload failed:', err);
    } finally {
      reuploadIndexRef.current = null;
      if (hiddenUploadRef.current) {
        hiddenUploadRef.current.value = '';
      }
    }
  };

  return (
    <>
      <Form form={form} layout="vertical" size="small">
        <BaseRow gutter={[16, 16]}>
          <BaseCol xs={24} md={12} lg={8}>
            <Form.Item name="fileName" label="File Name" rules={[{ required: true }]}>
              <Input placeholder="Enter file name" />
            </Form.Item>
          </BaseCol>
          <BaseCol xs={24} md={12} lg={12}>
            <Form.Item
              name="file"
              label="Upload File"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              rules={[{ required: true }]}
            >
              <Upload beforeUpload={() => false} maxCount={1}>
                <Button icon={<UploadOutlined />} size="small">
                  Choose File
                </Button>
              </Upload>
            </Form.Item>
          </BaseCol>
        </BaseRow>
        <Button type="primary" size="small" onClick={handleSubmit}>
          Submit
        </Button>
      </Form>

      <input
        type="file"
        accept=".pdf,.jpg,.png"
        ref={hiddenUploadRef}
        style={{ display: 'none' }}
        onChange={handleHiddenFileChange}
      />

      {uploadedFiles.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Text strong>Uploaded Files:</Text>
          <List
            bordered
            dataSource={uploadedFiles}
            style={{ marginTop: 8 }}
            renderItem={(item, index) => (
              <List.Item
                actions={[
                  <Button icon={<EyeOutlined />} size="small" onClick={() => handleView(item.file)} key="view">
                    View
                  </Button>,
                  <Button icon={<EditOutlined />} size="small" onClick={() => handleReuploadClick(index)} key="edit">
                    Re-upload
                  </Button>,
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    size="small"
                    onClick={() => handleRemove(index)}
                    key="remove"
                  >
                    Remove
                  </Button>,
                ]}
              >
                <Text>{item.name}</Text>
              </List.Item>
            )}
          />
        </div>
      )}
    </>
  );
};

export default DocUploadInFRS;
